import { Center, Environment, GizmoHelper, GizmoViewport, Grid, OrbitControls, PerspectiveCamera } from "@react-three/drei";
import { Canvas, useThree } from "@react-three/fiber";
import Model from "./Model";
import UnifiedModel from "./UnifiedModel";
import LoadStatus from "./LoadStatus";
import { Suspense, useState, useEffect, useRef } from "react";
import WorldScaleRef from "./WorldScaleRef";
import UVCheckerPlane from "./UVCheckerPlane";
import { ACESFilmicToneMapping, AgXToneMapping, NeutralToneMapping, CineonToneMapping, LinearToneMapping, ReinhardToneMapping  } from "three";
import Outliner from "./Outliner";
import { Collapse } from "antd";
import InfoPanel from "./InfoPanel";
import DebugPanel from "./DebugPanel";
import { useLocation } from 'react-router-dom';
import { CONFIGURATOR_INSPECTOR } from "../Constants";
import EnvironmentHandler from "./EnvironmentHandler";
import InteractionHintAnimator from "./InteractionHintAnimator";

const toneMappingOptions = {
    "Neutral" : NeutralToneMapping,
    "AGX" : AgXToneMapping,
    "ACES" : ACESFilmicToneMapping,
    "Cineon" : CineonToneMapping, 
    "Linear" : LinearToneMapping,
    "Reinhard" : ReinhardToneMapping,
}

const hdrEnvironmentOptions = {
    "Neutral": "./env/neutral.hdr",
    "Studio": "./env/Studio.hdr",
    "Jewelry": "./env/Jewelry.hdr"
};

const companyHdrEnvironmentOptions = {
    "Away-Travel": "./env/AwayTravels_Blurred.hdr",
    "Leather": "./env/NathanAnthony_Leather.hdr"
};

const isDirectionalLightHdr = {
    "Neutral": true
}

const defaultPatternOptions = [
  { value: "pattern1", image: "./textures/pattern1.jpg" },
  { value: "pattern2", image: "./textures/pattern2.jpg" },
  { value: "pattern3", image: "./textures/pattern3.jpg" },
];

const Model_Inspector = (props) => {

    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    let gizmoQuery = queryParams.get('hide_gizmo');
    let autoRotationQuery = queryParams.get('auto_rotation');
    let autoRotationSpeedQuery = queryParams.get('auto_rotation_speed');
    let environmentHDRQuery = queryParams.get('env');
    let hideGizmo = false;
    let autoRotation = false;
    let autoRotationSpeed = 1;
    let defaultEnvironmentHDR = "Neutral"
    let patternOptions = defaultPatternOptions;
    let selectedHDR = environmentHDRQuery ?? props.hdri;
    if (gizmoQuery) {
        hideGizmo = gizmoQuery.toLowerCase() === 'true';
    }
    if (autoRotationQuery) {
        autoRotation = autoRotationQuery.toLowerCase() === 'true';
    }
    if (autoRotationSpeedQuery) {
        autoRotationSpeed = parseFloat(autoRotationSpeedQuery);
    }
    if (props.patternOptions && props.patternOptions?.length > 0) {
        patternOptions =  props.patternOptions;
    }
    if (selectedHDR && (hdrEnvironmentOptions[selectedHDR] || companyHdrEnvironmentOptions[selectedHDR])) {
        defaultEnvironmentHDR = selectedHDR

        if (companyHdrEnvironmentOptions[selectedHDR]) {
            hdrEnvironmentOptions[selectedHDR] = companyHdrEnvironmentOptions[selectedHDR]
        }
    }

    const refModel = './models/RealWorldScaleRef.glb';

    const [loaded, setLoaded] = useState(false);
    const [modelSize, setModelSize] = useState(null);
    const [modelDimensionZ, setModelDimensionZ] = useState(null);
    const [maxSide, setMaxSide] = useState(0);
    const [distance, setDistance] = useState(0.5);
    const [originalDistance, setOriginalDistance] = useState(null);
    const [enableGrid, setEnableGrid] = useState(false);

    const [treeData, setTreeData] = useState(null);
    const [visibleNodes, setVisibleNodes] = useState(undefined);
    const [selectedNode, setSelectedNode] = useState(undefined);
    const [modelMetadata, setModelMetadata] = useState({});

    const [wireframeMode, setWireframeMode] = useState(false);
    const [wireframeOpacity, setWireframeOpacity] = useState(0.5);
    const [wireframeThickness, setWireframeThickness] = useState(1.0);
    const [uvMode, setUVMode] = useState(false);
    const [uvScale, setUVScale] = useState(1.0);
    const [patternsMode, setPatternsMode] = useState(false);
    const [configuratorMode, setConfiguratorMode] = useState(false);
    const [selectedPattern, setSelectedPattern] = useState(patternOptions[0].value);
    const [selectedPatternRotation, setSelectedPatternRotation] = useState(0);
    const [selectedMaterialNode, setSelectedMaterialNode] = useState(null);
    const [materialNodes, setMaterialNodes] = useState(null);
    const [backgroundColor, setBackgroundColor] = useState("#ffffff");
    const [xRepeat, setXRepeat] = useState(0.1);
    const [yRepeat, setYRepeat] = useState(0.1);
    const [repeatLock, setRepeatLock] = useState(true);
    const [uvSliderVisibility, setUVSliderVisibility] = useState(false);
    const [autoRotate, setAutoRotate] = useState(autoRotation);
    const [toneMapping, setToneMapping] = useState("Neutral");
    const [glToneMapping, setGLToneMapping] = useState(toneMappingOptions["Neutral"])
    const [environmentHDR, setEnvironmentHDR] = useState(defaultEnvironmentHDR);
    const [configuratorOptions, setConfigurationOptions] = useState([]);
    const [interactionHintEnabled, setInteractionHintEnabled] = useState(true);
    const [showInteractionHint , setShowInteractionHint ] = useState(true);

    const [nodeClass, setNodeClass] = useState(undefined);
    const [vertexCount, setVertexCount] = useState(undefined);
    const [polygonCount, setPolygonCount] = useState(undefined);
    const [materialName, setMaterialName] = useState(undefined);
    const [materialClass, setMaterialClass] = useState(undefined);
    const [quadsCount, setQuadsCount] = useState(undefined);
    const [triCount, setTriCount] = useState(undefined);
    const [ngonsCount, setNgonsCount] = useState(undefined);
    const [modifiers, _setModifiers] = useState(undefined);
    const [texelDensity, setTexelDensity] = useState(undefined);

    const gridRef = useRef();
    const orbitControlsRef = useRef();
    const getPaddingStyles = (target) => {
        if (!props.paddings || !Array.isArray(props.paddings)) return {};
        
        const targetPadding = props.paddings.find(p => p.target === target);
        if (!targetPadding) return {};
        
        const styles = {};
        if (targetPadding.paddingTop) styles.paddingTop = `${targetPadding.paddingTop}px`;
        if (targetPadding.paddingBottom) styles.paddingBottom = `${targetPadding.paddingBottom}px`;
        if (targetPadding.paddingLeft) styles.paddingLeft = `${targetPadding.paddingLeft}px`;
        if (targetPadding.paddingRight) styles.paddingRight = `${targetPadding.paddingRight}px`;
        
        return styles;
    };

    const getGizmoMargin = () => {
        const gizmoPadding = getPaddingStyles('gizmo');
        const baseMargin = 100;
        
        const rightMargin = baseMargin + (gizmoPadding.paddingRight ? parseInt(gizmoPadding.paddingRight) : 0);
        const bottomMargin = baseMargin + (gizmoPadding.paddingBottom ? parseInt(gizmoPadding.paddingBottom) : 0);
        
        return [rightMargin, bottomMargin];
    };

    useEffect(() => {
        if (props.metadata) {
            let metadata = props.metadata["metadata"];
            if (metadata["low"]) {
                setModelMetadata(metadata["low"])
            }
            else if (metadata["high"]) {
                setModelMetadata(metadata["high"])
            }
        }
    }, [props.metadata]);

    useEffect(() => {
        if (props.groupProducts) {
            setConfigurationOptions(props.groupProducts);
        }
    }, [props.groupProducts]);

    // Set initial camera position when distance changes
    useEffect(() => {
        if (distance > 0 && orbitControlsRef.current && !originalDistance) {
            const camera = orbitControlsRef.current.object;
            camera.position.set(0, 0, distance);
            orbitControlsRef.current.update();
        }
    }, [distance, originalDistance]);

    useEffect(() => {
        if (!loaded) return;
    
        const canvas = document.querySelector('.canvas-container canvas');
        if (!canvas) return;
    
        let isHolding = false;
    
        const handleMouseDown = () => {
            isHolding = true;
            setInteractionHintEnabled(false);
        };
    
        const handleMouseUp = () => {
            if (isHolding) {
                isHolding = false;
            }
        };
    
        const handleTouchStart = () => {
            setInteractionHintEnabled(false);
        };
    
        const handleTouchEnd = () => {};
    
        canvas.addEventListener("mousedown", handleMouseDown);
        canvas.addEventListener("mouseup", handleMouseUp);
        canvas.addEventListener("touchstart", handleTouchStart);
        canvas.addEventListener("touchend", handleTouchEnd);
    
        return () => {
            canvas.removeEventListener("mousedown", handleMouseDown);
            canvas.removeEventListener("mouseup", handleMouseUp);
            canvas.removeEventListener("touchstart", handleTouchStart);
            canvas.removeEventListener("touchend", handleTouchEnd);
        };
    }, [loaded]);
       
    useEffect(() => {
        if (configuratorMode) {
            setInteractionHintEnabled(false);
        }
    }, [configuratorMode])
    
    const ScreenshotHandler = ({ loaded }) => {
        const { gl, scene, camera } = useThree();

        useEffect(() => {
            const handleMessage = (event) => {
                if (!loaded || !event.data?.getBlob || !camera || !scene || !gl) return;

                if (gridRef.current) gridRef.current.visible = false;

                // Force render
                gl.render(scene, camera);

                const imgData = gl.domElement.toDataURL('image/png');

                if (gridRef.current) gridRef.current.visible = true;

                // Post image blob back
                event.source?.postMessage({
                    blob_data: {
                        id: event.data.getBlob,
                        blob: imgData,
                    },
                }, event.origin);
            };

            window.addEventListener('message', handleMessage);
            return () => window.removeEventListener('message', handleMessage);
        }, [gl, camera, scene, loaded]);

        return null;
    };

    const modelSizeSet = (size) => {
        const width = size.x;
        const height = size.y;
        const depth = size.z;
      
        setMaxSide(Math.max(width, height, depth));
        setModelSize(width);
        setModelDimensionZ(depth);
      
        const aspect = window.innerWidth / window.innerHeight;
      
        const vFov = 20 * (Math.PI / 180); // vertical FOV
        const hFov = 2 * Math.atan(Math.tan(vFov / 2) * aspect);
      
        // Compute distances needed to fit both width and height
        const distanceForWidth = (width / 2) / Math.tan(hFov / 2);
        const distanceForHeight = (height / 2) / Math.tan(vFov / 2);
      
        // Add depth factor (for round or deep objects)
        const depthOffset = depth / 2;
      
        // Pick the larger required distance and add margin
        const distance = Math.max(distanceForWidth, distanceForHeight) + depthOffset;
      
        setDistance(distance * 1.35);
        setEnableGrid(props.debugMode);
      };

    const onDebugWireframe = (e) => {
        setWireframeMode(e.target.checked);
    };

    const onWireframeOpacityChange = (value) => {
        if (isNaN(value)) {
            return;
        }
        setWireframeOpacity(value);
    };

    const onWireframeThicknessChange = (value) => {
        if (isNaN(value)) {
            return;
        }
        setWireframeThickness(value);
    };

    const onDebugUV = (e) => {
        const isUVMode = e.target.checked;
        setUVMode(isUVMode);
        setUVSliderVisibility(isUVMode);
        
        if (isUVMode) {
            // Get the current actual camera distance from OrbitControls
            if (orbitControlsRef.current && originalDistance === null) {
                const currentDistance = orbitControlsRef.current.getDistance();
                setOriginalDistance(currentDistance);
                
                // Calculate new target distance
                const uvZoomOutFactor = 1.3;
                const targetDistance = currentDistance * uvZoomOutFactor;
                
                // Smoothly animate to new distance by manipulating camera position
                const camera = orbitControlsRef.current.object;
                const target = orbitControlsRef.current.target;
                
                // Get direction from target to camera
                const direction = camera.position.clone().sub(target).normalize();
                
                // Set new camera position at target distance
                const newPosition = target.clone().add(direction.multiplyScalar(targetDistance));
                camera.position.copy(newPosition);
                
                // Update the controls
                orbitControlsRef.current.update();
            }
        } else {
            // Restore original distance when UV mode is turned off
            if (originalDistance !== null && orbitControlsRef.current) {
                const camera = orbitControlsRef.current.object;
                const target = orbitControlsRef.current.target;
                
                // Get direction from target to camera
                const direction = camera.position.clone().sub(target).normalize();
                
                // Set camera position back to original distance
                const newPosition = target.clone().add(direction.multiplyScalar(originalDistance));
                camera.position.copy(newPosition);
                
                // Update the controls and reset stored distance
                orbitControlsRef.current.update();
                setOriginalDistance(null);
            }
        }
    };

    const onDebugPatterns = (e) => {
        setPatternsMode(e.target.checked);
    };

    const onTabChange = (e) => {
        if (e == '2') {
            setConfiguratorMode(true);
        }
        else {
            setConfiguratorMode(false);
        }
    };

    const onUVScaleChange = (value) => {
        if (isNaN(value)) {
            return;
        }
        setUVScale(value);
    };

    const onXRepeatChange = (value) => {
        if (isNaN(value)) {
            return;
        }
        setXRepeat(value);
        if (repeatLock) {
            setYRepeat(value);
        }
    };

    const onYRepeatChange = (value) => {
        if (isNaN(value)) {
            return;
        }
        setYRepeat(value);
        if (repeatLock) {
            setXRepeat(value);
        }
    };

    const onChangePattern = (value) => {
        setSelectedPattern(value);
    };

    const onChangePatternRotation = () => {
        let currentRotation = selectedPatternRotation;
        setSelectedPatternRotation(currentRotation + Math.PI/2);
    };

    const onChangeMaterialNode = (value) => {
        setSelectedMaterialNode(value);
    };

    const onChangeRepeatLock = (e) => {
        const repeatLockVal = e.target.checked;
        setRepeatLock(repeatLockVal);
        if (repeatLockVal) {
            setYRepeat(xRepeat);
        }
    };

    const onSetMaterialNodes = (materialNodes) => {
        const matNodes = [...materialNodes]
        setMaterialNodes(matNodes);
        if (matNodes && matNodes.length > 0) {
            let selectMat = matNodes[0];
            for (let mat of matNodes) {
                if (mat && typeof mat === 'string' && mat.toLowerCase().includes("fabric")){
                    selectMat = mat;
                    break;
                }
            }
            if (selectMat) {
                onChangeMaterialNode(selectMat);
            }
        }
    }

    const onBackgroundColorChange = (backgroundColor) => {
        setBackgroundColor(backgroundColor);
    }

    const onAutoRotate = (e) => {
        setAutoRotate(e.target.checked);
    };

    const handleOnModelLoaded = (data) => {
        setTreeData(data);
        setLoaded(true);
        if (props.onLoadingStatus) {
            props.onLoadingStatus('ready');
        }
    };

    const handleOnModelError = (errorMessage) => {
        setLoaded(false);
        if (props.onLoadingStatus) {
            props.onLoadingStatus('error', errorMessage);
        }
    };

    const onConfiguratorSelect = (model) => {
        sendMessageToConfigurator('loadVariation=model:' + model);
    }

    const sendMessageToConfigurator = (message) => {
        const iframe = document.getElementById('configurator-iframe').contentWindow;
        iframe.postMessage(message, '*');
    };

    const handleVisibiltyChanged = (_visibleNodes) => {
        setVisibleNodes(_visibleNodes);
    };

    const setModifiers = (mods) => {
        let modifiersString = "";

        if (mods == undefined || mods.length == 0) {
            _setModifiers(undefined);
        }
        else {
            for (let i = 0; i < mods.length; i++) {

                modifiersString += mods[i];

                if (i < mods.length - 1) {
                    modifiersString += " ,";
                }
            }
            _setModifiers(modifiersString);
        }
    };

    const onSelectionChanged = (selection) => {
        if (selection && modelMetadata) {
            setSelectedNode(selection);
            let selectionMetadata = modelMetadata[selection];
            if (selectionMetadata) {
                setNodeClass(selectionMetadata.class);
                setVertexCount(selectionMetadata.vertex_count);
                setPolygonCount(selectionMetadata.polygon_count);
                setQuadsCount(selectionMetadata.quads);
                setTriCount(selectionMetadata.tri);
                setNgonsCount(selectionMetadata.ngons);
                setMaterialName(selectionMetadata.material_name);
                setMaterialClass(selectionMetadata.material_class);
                setModifiers(selectionMetadata.modifiers);
                setTexelDensity(selectionMetadata.texel_density);
            }
            else {
                setSelectedNode(undefined);
            }
        }
        else {
            setSelectedNode(undefined);
        }
    }

    const renderModel = () => {
        const commonProps = {
            uvChecker: uvMode,
            wireframe: wireframeMode,
            wireframeOpacity: wireframeOpacity,
            wireframeThickness: wireframeThickness,
            uvScale: uvScale,
            patternsMode: patternsMode,
            selectedPattern: selectedPattern,
            selectedPatternRotation: selectedPatternRotation,
            selectedMaterialNode: selectedMaterialNode,
            xRepeat: xRepeat,
            yRepeat: yRepeat,
            onSetMaterialNodes: onSetMaterialNodes,
            patternOptions: patternOptions,
            backgroundColor: backgroundColor,
            setModelSize: modelSizeSet,
            metadata: modelMetadata,
            onModelLoaded: handleOnModelLoaded,
            onModelError: handleOnModelError,
            visibleNodes: visibleNodes,
            selectedNode: selectedNode,
            onLoadingStatus: props.onLoadingStatus
        };

        const modelType = props.modelType || 'glb';

        if (modelType === 'unified') {
            return <UnifiedModel {...commonProps} url={props.modelUrl} />;
        } else {
            return <Model {...commonProps} url={props.modelUrl} />;
        }
    };

    return (
        <>
            <div className={`canvas-container ${loaded ? 'loaded' : 'loading'}`}>
                {interactionHintEnabled && showInteractionHint && loaded && (
                    <div className="interaction-hint">
                            <img src="./img/pointer-icon.svg" alt="Interaction Hint" className="interaction-hint-icon" />
                    </div>
                )}
                {configuratorMode ? <div id="configurator-container">
                    <iframe
                        id="configurator-iframe"
                        src={CONFIGURATOR_INSPECTOR}
                        frameBorder="0"
                        title="3D Configurator"
                        onLoad={() => {
                            sendMessageToConfigurator('changeMode=custom');
                            onConfiguratorSelect(props.modelUrl);
                        }}
                    />
                </div>
                :
                <Canvas
                    gl={{ toneMapping: glToneMapping, toneMappingExposure: 1 }}
                    flat
                    shadows>
                    <color attach="background" args={[backgroundColor]} />
                    <PerspectiveCamera
                        fov={20}
                        position={[0, 0, distance]}
                        near={0.01}
                        makeDefault
                    />

                 
                    <InteractionHintAnimator 
                        showInteractionHint={interactionHintEnabled && showInteractionHint } 
                        orbitControlsRef={orbitControlsRef} 
                        setShowInteractionHint = {setShowInteractionHint} 
                    />

                    <Suspense fallback={null}>
                        <EnvironmentHandler environmentKey={environmentHDR} options={hdrEnvironmentOptions} />
                        {isDirectionalLightHdr[environmentHDR] && <directionalLight 
                            position={[0, -maxSide * 8, 0]} 
                            intensity={1.6} 
                            color="#ffffff"
                            castShadow={true}
                            shadow-mapSize-width={2048}
                            shadow-mapSize-height={2048}
                        />}
                        <Center>
                            {renderModel()}
                            {uvMode && <WorldScaleRef modelSize={modelSize} modelRef={refModel} />}
                            {enableGrid ? <Grid ref={gridRef} infiniteGrid fadeDistance={7.0} cellSize={0.05} /> : ''}
                        </Center>
                        {uvMode && <UVCheckerPlane modelSize={modelSize} modelDimensionZ={modelDimensionZ} orbitControlsRef={orbitControlsRef} />}
                        {
                            !hideGizmo && (
                                <GizmoHelper 
                                    alignment="bottom-right" 
                                    margin={getGizmoMargin()}
                                >
                                    <GizmoViewport labelColor="white" axisHeadScale={1} />
                                </GizmoHelper>
                            )
                        }
                    <ScreenshotHandler loaded={loaded} />
                    <OrbitControls
                        makeDefault
                        autoRotate={autoRotate}
                        minDistance={maxSide * 1.2}
                        maxDistance={Math.max(maxSide * 8, distance * 2)}
                        autoRotateSpeed={autoRotationSpeed}
                        enablePan={true}
                        enableDamping={true} 
                        ref={orbitControlsRef}
                    />
                    </Suspense>
                </Canvas>}
            </div>
            {!loaded && (
                <div className="loading-overlay">
                    <LoadStatus loadingText={"Loading Model..."} />
                </div>
            )}
            {props.debugMode ? 
                <div className="inspectorPanel" style={getPaddingStyles('debugPanel')}>
                    <Collapse>
                        <Collapse.Panel style={{ fontSize: "small" , backgroundColor:"#ffffff" }} header="Debug Modes" key="1">
                            <DebugPanel
                                selectedProducts={props.selectedProducts}
                                onAutoRotate={onAutoRotate}
                                onDebugWireframe={onDebugWireframe}
                                wireframeMode={wireframeMode}
                                wireframeOpacity={wireframeOpacity}
                                onWireframeOpacityChange={onWireframeOpacityChange}
                                wireframeThickness={wireframeThickness}
                                onWireframeThicknessChange={onWireframeThicknessChange}
                                onDebugUV={onDebugUV}
                                onDebugPatterns={onDebugPatterns}
                                onConfiguratorSelect={onConfiguratorSelect}
                                showConfiguratorMode={props.configuratorMode}
                                configuratorOptions={configuratorOptions}
                                onTabChange={onTabChange}
                                onUVScaleChange={onUVScaleChange}
                                onXRepeatChange={onXRepeatChange}
                                onYRepeatChange={onYRepeatChange}
                                onChangePattern={onChangePattern}
                                onChangePatternRotation={onChangePatternRotation}
                                onChangeMaterialNode={onChangeMaterialNode}
                                selectedPattern={selectedPattern}
                                selectedMaterialNode={selectedMaterialNode}
                                materialNodes={materialNodes}
                                backgroundColor={backgroundColor}
                                onBackgroundColorChange={onBackgroundColorChange}
                                uvSliderVisibility={uvSliderVisibility}
                                patternsMode={patternsMode}
                                uvScale={uvScale}
                                xRepeat={xRepeat}
                                yRepeat={yRepeat}
                                repeatLock={repeatLock}
                                onChangeRepeatLock={onChangeRepeatLock}
                                patternOptions={patternOptions}
                                setToneMapping={setToneMapping}
                                toneMapping={toneMapping}
                                setGLToneMapping={setGLToneMapping}
                                toneMappingOptions={toneMappingOptions}
                                environmentHDR={environmentHDR}
                                setEnvironmentHDR={setEnvironmentHDR}
                                hdrEnvironmentOptions={hdrEnvironmentOptions}
                            />
                        </Collapse.Panel>
                        <Collapse.Panel style={{ fontSize: "small", backgroundColor:"#ffffff" }} header="Hierarchy" key="2">
                            {treeData != null && <Outliner
                                data={treeData}
                                onVisibilityChanged={handleVisibiltyChanged}
                                onSelectionChanged={onSelectionChanged} />}
                        </Collapse.Panel>
                    </Collapse>

                </div>
            : ''}
        </>
    );
};
export default Model_Inspector;